import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { ArrowRight, ExternalLink } from "lucide-react";

const APPSOURCE_URL = "https://marketplace.microsoft.com/en-us/product/WA200010021?tab=Overview";

export const CTA = () => {
  const { ref: containerRef, animationClass: containerAnimation } = useScrollAnimation<HTMLDivElement>({
    type: 'scale',
    threshold: 0.2,
  });
  
  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="container px-4">
        <div 
          ref={containerRef}
          className={`relative overflow-hidden rounded-2xl md:rounded-3xl bg-gradient-hero p-8 md:p-12 lg:p-16 xl:p-20 shadow-elevated ${containerAnimation}`}
        >
          {/* Background Effects */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-primary-glow/30 via-transparent to-transparent" />
          
          {/* Animated Particles - Hidden on mobile for performance */}
          <div className="hidden md:block absolute inset-0 overflow-hidden">
            <div className="absolute top-10 left-10 w-4 h-4 bg-white/20 rounded-full animate-float" />
            <div className="absolute top-20 right-20 w-3 h-3 bg-white/30 rounded-full animate-float animation-delay-1000" />
            <div className="absolute bottom-20 left-1/4 w-2 h-2 bg-white/25 rounded-full animate-float animation-delay-2000" />
            <div className="absolute bottom-10 right-1/3 w-3 h-3 bg-white/20 rounded-full animate-float animation-delay-500" />
          </div>
          
          <div className="relative z-10 max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 text-primary-foreground">
              Ready to transform your contact management?
            </h2>
            
            <p className="text-lg sm:text-xl md:text-2xl text-primary-foreground/80 mb-8 md:mb-10">
              Get Email2Contact now from the Microsoft AppSource store.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto mb-6">
              <Button 
                size="lg" 
                variant="hero"
                asChild
                className="w-full sm:w-auto touch-target touch-active group relative overflow-hidden"
              >
                <a 
                  href={APPSOURCE_URL} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2"
                >
                  <span className="relative">Get it on AppSource</span>
                  <ExternalLink className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>
              </Button>
              
              <Button 
                size="lg" 
                variant="ghost" 
                asChild 
                className="border border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:border-primary-foreground/50 touch-target touch-active"
              >
                <Link to="/support">Talk to sales</Link>
              </Button>
            </div>
            
            <p className="mt-6 md:mt-8 text-sm text-primary-foreground/60">
              Free to try • No credit card required • Works inside Outlook
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
